import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    ValidationErrors,
    ValidatorFn,
    Validators
} from "@angular/forms";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { Dictionary } from "lodash";
import { NgxSpinnerService } from "ngx-spinner";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ExpressionEvaluatorService } from "src/app/core/service/expression/expression-evaluator-service.service";
import { SubscriptionService } from "src/app/core/service/subscription.service";
import { AddressDTO } from "src/app/data/schema/Common/AddressDTO";
import { CustomFieldsSelectValuesDTO } from "src/app/data/schema/Subscriptions/CustomFieldsSelectValuesDTO";
import { SelectCustomFieldsDTO } from "src/app/data/schema/Subscriptions/SelectCustomFieldsDTO";
import { UserCustomFieldDTO } from "src/app/data/schema/Subscriptions/UserCustomFieldDTO";
import { UserCustomFieldTypeDTO } from "src/app/data/schema/Subscriptions/UserCustomFieldTypeDTO";
import { UsuarioTO } from "src/app/data/schema/User/UsuarioTO";
import { ApiCustomFieldsService } from "src/app/data/service/custom-fields/api-custom-fields.service";
import { ApiUserService } from "src/app/data/service/user/api-user.service";
import { CNPJUtils } from "src/app/utils/CNPJUtils";
import { CPFUtils } from "src/app/utils/CPFUtils";
import { AddressFormComponent } from "../address-form/address-form.component";
import { ConfirmDialogComponent, ConfirmDialogData } from "../confirm-dialog/confirm-dialog.component";
import enUS from "./i18n/en-US.json";
import esES from "./i18n/es-ES.json";
import ptBR from "./i18n/pt-BR.json";

@Component({
    selector: "app-user-custom-fields",
    templateUrl: "./user-custom-fields.component.html",
    styleUrls: ["./user-custom-fields.component.scss"],
    providers: [ExpressionEvaluatorService]
})
export class UserCustomFieldsComponent implements OnInit, OnDestroy {
    separatorKeysCodes: number[] = [ENTER, COMMA];

    @Input("fields") fields: UserCustomFieldDTO[];
    @Input() editMode: boolean;
    @Input("user")
    set user(user: UsuarioTO) {
        this.userId = user?.id;
        if (user?.customFields) {
            this.createForm(user.customFields);
        }
    }

    @ViewChild("addressForm") addressFormComponent: AddressFormComponent;

    public userId: string;
    public form: UntypedFormGroup;
    public filesToUpload: Dictionary<HTMLInputElement> = {};
    public signedUrls: Dictionary<string> = {};
    public selectFields: SelectCustomFieldsDTO[] = [];
    public selectedOptions: CustomFieldsSelectValuesDTO[] = [];
    public Address: AddressDTO;

    private _unsubscribeAll = new Subject();
    constructor(
        private _formBuilder: UntypedFormBuilder,
        private _translateService: TranslateService,
        private dialog: MatDialog,
        private _apiUserService: ApiUserService,
        private _spinner: NgxSpinnerService,
        private _matSnackBar: MatSnackBar,
        private _customFieldsService: ApiCustomFieldsService,
        private evaluator: ExpressionEvaluatorService,
        private subscriptionService: SubscriptionService
    ) {
        this._translateService.setTranslation("pt-BR", ptBR, true);
        this._translateService.setTranslation("en-US", enUS, true);
        this._translateService.setTranslation("es-ES", esES, true);
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.complete();
    }

    ngOnInit(): void {
        const group = {};
        this.fields = this.fields.sort((a, b) => a.order - b.order);
        this.fields.forEach((f) => {
            const validators = this.generateValidators(f);

            if (f.type != UserCustomFieldTypeDTO.Address)
                group[f.key] = new UntypedFormControl("", Validators.compose(validators));

            //unica forma de desvinvular "fields" de "selectFields". Caso contrário qualquer alteração em um array reflete nos dois
            if (f.type == UserCustomFieldTypeDTO.Select || f.type == UserCustomFieldTypeDTO.MultiSelect) {
                this.selectFields.push({
                    fieldName: f.fieldName,
                    key: f.key,
                    fileTypes: null,
                    required: f.required,
                    showOnRegister: f.showOnRegister,
                    type: f.type,
                    useAsUsername: f.useAsUsername,
                    options: null,
                    id: f.id,
                    conditionToShow: f.conditionToShow,
                    order: f.order
                });
                this.GetSelectOptions(f.id, null);
            }
        });

        this.form = this._formBuilder.group(group);
        this.setupFieldVisibilityTracking();
    }

    private setupFieldVisibilityTracking(): void {
        this.validateFieldsToShow();
        this.form.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.validateFieldsToShow();
            });
    }

    createForm(userFields?: Dictionary<string>): void {
        const group = {};
        this.filesToUpload = {};

        this.fields.forEach((f) => {
            const validators = this.generateValidators(f);

            let value = userFields ? userFields[f.fieldName] ?? "" : "";

            if (value && value != "" && f.type == "File" && this.userId) {
                this._apiUserService
                    .getUrlFileCustomField(this.userId, f.fieldName)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe((url) => {
                        console.log("url", url);
                        this.signedUrls[f.fieldName] = url;

                        const key = f.key;
                        if (key && this.form?.controls[key]) {
                            const rawValue = userFields ? userFields[f.fieldName] : null;
                            const formValue = rawValue !== undefined && rawValue !== "" ? rawValue : null;
                            this.form.controls[key].setValue(formValue);
                            this.form.controls[key].updateValueAndValidity();
                        }
                    });
            }

            if (f.type == UserCustomFieldTypeDTO.MultiSelect) {
                if (value) {
                    value = value.split(",");
                    value.forEach((v) => {
                        this.selectedOptions.push({
                            fieldId: f.id,
                            fieldName: f.fieldName,
                            id: null,
                            key: v,
                            value: v,
                            subscriptionId: null
                        });
                    });
                }
            }

            if (f.type != UserCustomFieldTypeDTO.Address)
                group[f.key] = new UntypedFormControl(value, Validators.compose(validators));
        });

        this.form = this._formBuilder.group(group);

        this.Address = {
            city: userFields["Address_City"] ?? "",
            complement: userFields["Address_Complement"] ?? "",
            country: userFields["Address_Country"] ?? "",
            neighborhood: userFields["Address_Neighborhood"] ?? "",
            number: userFields["Address_Number"] ?? "",
            state: userFields["Address_State"] ?? "",
            street: userFields["Address_Street"] ?? "",
            zipCode: userFields["Address_ZipCode"] ?? ""
        };

        this.setupFieldVisibilityTracking();
    }

    validateFieldsToShow(): void {
        this.fields.forEach((field) => {
            if (!field.conditionToShow) field.show = true;
            else field.show = this.showField(field.conditionToShow);

            this.form.controls[field.key]?.setValidators(this.generateValidators(field));
        });
    }

    getFileUrl(userId: string, fieldName: string): void {
        this._spinner.show("loadingFile");
        this._apiUserService
            .getUrlFileCustomField(userId, fieldName)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(
                (url) => {
                    this.signedUrls[fieldName] = url;
                    this._spinner.hide("loadingFile");
                },
                () => {
                    this._spinner.hide("loadingFile");
                }
            );
    }

    public exportValues(): Dictionary<string> {
        const retorno: Dictionary<string> = {};

        this.fields.forEach((f) => {
            if (f.type == UserCustomFieldTypeDTO.MultiSelect) {
                retorno[f.fieldName] = this.form.get(f.key).value ? this.form.get(f.key).value?.join(",") : "";
            } else if (f.type == UserCustomFieldTypeDTO.Address) {
                const address = this.addressFormComponent.exportValues();

                retorno["Address_City"] = address.city;
                retorno["Address_Complement"] = address.complement;
                retorno["Address_Country"] = address.country;
                retorno["Address_Neighborhood"] = address.neighborhood;
                retorno["Address_Number"] = address.number;
                retorno["Address_State"] = address.state;
                retorno["Address_Street"] = address.street;
                retorno["Address_ZipCode"] = address.zipCode;
            } else retorno[f.fieldName] = this.form.get(f.key).value;
        });

        return retorno;
    }

    generateValidators(f: UserCustomFieldDTO): ValidatorFn[] {
        const validators = [];

        if (f.required && f.show) validators.push(Validators.required);

        switch (f.type) {
            case UserCustomFieldTypeDTO.CNPJ:
                validators.push(this.cnpjInvalido);
                break;
            case UserCustomFieldTypeDTO.CPF:
                validators.push(this.cpfjInvalido);
                break;

            default:
                break;
        }
        return validators;
    }

    cnpjInvalido: ValidatorFn = (control: UntypedFormGroup): ValidationErrors | null => {
        return control.value && control.dirty && !CNPJUtils.ValidarCNPJ(control.value) ? { cnpjInvalido: true } : null;
    };

    cpfjInvalido: ValidatorFn = (control: UntypedFormGroup): ValidationErrors | null => {
        return control.value && control.dirty && !CPFUtils.ValidarCPF(control.value) ? { cpfInvalido: true } : null;
    };

    setFile(fieldName: string, input: HTMLInputElement): void {
        if (this.userId) {
            this._spinner.show("uploadingFile");

            this._apiUserService
                .uploadFileCustomField(this.userId, input.files[0], fieldName)
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(
                    () => {
                        this._spinner.hide("uploadingFile");
                        this.getFileUrl(this.userId, fieldName);

                        // Marca o campo de formulário como preenchido para satisfazer o Validators.required
                        const key = this.fields.find((f) => f.fieldName == fieldName)?.key;
                        if (key && this.form?.controls[key]) {
                            const fileName = input?.files?.[0]?.name || "uploaded";
                            this.form.controls[key].setValue(fileName);
                            this.form.controls[key].markAsDirty();
                            this.form.controls[key].markAsTouched();
                            this.form.controls[key].updateValueAndValidity();
                        }

                        this._translateService
                            .get("USER_CUSTOM_FIELDS.FILE_UPLOADED")
                            .pipe(takeUntil(this._unsubscribeAll))
                            .subscribe((res) => {
                                this._matSnackBar.open(res, "OK", {
                                    verticalPosition: "top",
                                    duration: 2000
                                });
                            });
                    },
                    () => {
                        this._translateService
                            .get("USER_CUSTOM_FIELDS.FILE_UPLOAD_ERROR")
                            .pipe(takeUntil(this._unsubscribeAll))
                            .subscribe((res) => {
                                this._matSnackBar.open(res, "OK", {
                                    verticalPosition: "top",
                                    duration: 2000
                                });
                            });

                        this._spinner.hide("uploadingFile");
                    }
                );
        } else {
            this.filesToUpload[fieldName] = input;
            this.signedUrls[fieldName] = null;

            // Sem userId (usuário não persistido ainda), ainda assim marcar o campo como preenchido
            const key = this.fields.find((f) => f.fieldName == fieldName)?.key;
            if (key && this.form?.controls[key]) {
                const fileName = input?.files?.[0]?.name || "uploaded";
                this.form.controls[key].setValue(fileName);
                this.form.controls[key].markAsDirty();
                this.form.controls[key].markAsTouched();
                this.form.controls[key].updateValueAndValidity();
            }
        }
    }

    deleteFile(fieldName): void {
        let _title;
        this._translateService
            .get("USER_CUSTOM_FIELDS.REMOVE_FILE_TITLE")
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((res) => {
                _title = res;
            });

        let _msg;
        this._translateService
            .get("USER_CUSTOM_FIELDS.REMOVE_FILE")
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((res) => {
                _msg = res;
            });

        const config: MatDialogConfig<ConfirmDialogData> = {
            data: {
                title: _title,
                msg: _msg
            }
        };

        const dialogRef = this.dialog.open(ConfirmDialogComponent, config);

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((result) => {
                if (!result) return;
                this.filesToUpload[fieldName] = null;
                const key = this.fields.find((f) => f.fieldName == fieldName).key;
                this.form.get(key).setValue(null);

                if (this.userId)
                    this._apiUserService
                        .deleteFileCustomField(this.userId, fieldName)
                        .pipe(takeUntil(this._unsubscribeAll))
                        .subscribe(() => (this.signedUrls[fieldName] = null));
            });
    }

    GetSelectOptions(fieldId: string, filter: string): void {
        this._spinner.show("select");
        this._customFieldsService
            .GetSelectValues(this.subscriptionService?.currentSubscription?.id, fieldId, 0, 50, filter)
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(
                (res) => {
                    this.selectFields.find((x) => x.id == fieldId).options = res;
                    this._spinner.hide("select");
                },
                () => {
                    this._spinner.hide("select");
                }
            );
    }

    GetSelectOptionsToList(fieldName: string): CustomFieldsSelectValuesDTO[] {
        return this.selectFields.find((x) => x.fieldName == fieldName)?.options?.data ?? [];
    }

    GetSelected(fieldName: string): CustomFieldsSelectValuesDTO[] {
        return this.selectedOptions.filter((x) => x.fieldName == fieldName) ?? [];
    }

    selectOption(option: CustomFieldsSelectValuesDTO): void {
        this.selectedOptions.push(option);
        this.setMultiSelectFormValue(option.fieldName);
    }

    removeSelected(option: CustomFieldsSelectValuesDTO): void {
        const i = this.selectedOptions.indexOf(option);
        this.selectedOptions.splice(i, 1);
        this.setMultiSelectFormValue(option.fieldName);
    }

    setMultiSelectFormValue(fieldName: string): void {
        const values = this.GetSelected(fieldName).map((c) => c.value);

        const key = this.fields.find((f) => f.fieldName == fieldName).key;
        this.form.controls[key].setValue(values);
    }

    showField(expression: string): boolean {
        if (!expression) return true;

        const res = this.evaluator.evaluate(expression, this.form.value);
        return res;
    }
}
